/**
 * Emissions tab aggregation: one pass over the period's ASNs feeds the cards,
 * the pies and Impact Over Time, so all three describe the same shipments.
 *
 * CR-8240: Impact Over Time bucketed only ASNs with a `shipment_date`, which no
 * UI flow writes, so it read "No data" beside cards showing distance. An ASN
 * without one now counts in the month it was raised — the same effective date
 * the backend's `shipment_date_after` / `shipment_date_before` window uses.
 * Months also sort by year: "Jan 26" did not parse, so a period spanning a year
 * end kept the wrong six months.
 */

export interface TransportBreakdownItem {
  mode: string
  emissions: number
  distance: number
  percentage: number
  [key: string]: unknown
}

export interface SupplierEmissionItem {
  supplier: string
  fullName: string
  emissions: number
  percentage: number
  [key: string]: unknown
}

export interface EmissionsDisplayData {
  total: number
  byTransport: TransportBreakdownItem[]
  bySupplier: SupplierEmissionItem[]
  byMonth: Array<{ month: string; air: number; sea: number; rail: number; road: number; total: number }>
  trend: Array<{ month: string; emissions: number; trend_line: number }>
}

const MODES = ['air', 'sea', 'rail', 'road'] as const

/**
 * The ASN's effective shipment month as `YYYY-MM`: its shipment date, else the
 * day it was raised (the server-local date the API sends), else null.
 *
 * @param asn - One ASN row from `/purchasing/asns/`
 * @returns `YYYY-MM`, or null when the ASN carries neither date
 */
export function shipmentMonth(asn: Record<string, unknown>): string | null {
  const raw = (asn['shipment_date'] || asn['created_at']) as string | undefined
  return raw && /^\d{4}-\d{2}/.test(raw) ? raw.slice(0, 7) : null
}

/** "2026-01" → "Jan 26": the label shape `parseMonthToDateRange` reads back. */
function monthLabel(isoMonth: string): string {
  const [year, month] = isoMonth.split('-').map(Number)
  // eslint-disable-next-line no-restricted-syntax -- CR-6307: aggregation key, not a display render
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

/**
 * Totals, transport and supplier splits, and the last six months of ASN emissions.
 *
 * @param asns - Every ASN in the period (all pages)
 * @returns The data the Emissions tab renders
 */
export function aggregateEmissions(asns: Array<Record<string, unknown>>): EmissionsDisplayData {
  const byTransport: Record<string, { emissions: number; distance: number }> = {}
  MODES.forEach((m) => { byTransport[m] = { emissions: 0, distance: 0 } })
  const bySupplier: Record<string, number> = {}
  const byMonth: Record<string, Record<string, number>> = {}

  asns.forEach((asn) => {
    const transport = ((asn['method_of_transport'] as string) || 'road').toLowerCase()
    const emissions = parseFloat(String(asn['carbon_emission_kg'] || 0)) || parseFloat(String(asn['calculated_carbon_emission'] || 0)) || 0
    const distance = parseFloat(String(asn['distance_km'] || 0)) || 0
    if (byTransport[transport]) {
      byTransport[transport].emissions += emissions
      byTransport[transport].distance += distance
    }
    // CR-8240: the list sends `supplier` as an id and the name under
    // `supplier_details`; reading only the first two keys made every supplier
    // "Unknown". They stay as fallbacks for older payload shapes.
    const details = asn['supplier_details'] as Record<string, unknown> | undefined
    const supplierObj = asn['supplier'] as Record<string, unknown> | undefined
    const supplierName = (details?.['name'] as string) || (asn['supplier_name'] as string)
      || (supplierObj?.['name'] as string) || 'Unknown'
    bySupplier[supplierName] = (bySupplier[supplierName] || 0) + emissions

    const month = shipmentMonth(asn)
    if (month) {
      if (!byMonth[month]) byMonth[month] = { air: 0, sea: 0, rail: 0, road: 0 }
      if (byMonth[month][transport] !== undefined) byMonth[month][transport] += emissions
    }
  })

  const total = Object.values(byTransport).reduce((sum, t) => sum + t.emissions, 0)
  const share = (n: number) => (total > 0 ? (n / total) * 100 : 0)

  const monthly = Object.keys(byMonth).sort().slice(-6).map((iso) => {
    const m = byMonth[iso]
    return {
      month: monthLabel(iso),
      air: m['air'] || 0, sea: m['sea'] || 0, rail: m['rail'] || 0, road: m['road'] || 0,
      total: (m['air'] || 0) + (m['sea'] || 0) + (m['rail'] || 0) + (m['road'] || 0),
    }
  })

  return {
    total,
    byTransport: Object.entries(byTransport).map(([mode, d]) => ({
      mode, emissions: d.emissions, distance: d.distance, percentage: share(d.emissions),
    })),
    bySupplier: Object.entries(bySupplier)
      .sort((a, b) => b[1] - a[1])
      .map(([name, emissions]) => ({
        supplier: name.length > 15 ? name.substring(0, 15) + '...' : name,
        fullName: name,
        emissions,
        percentage: share(emissions),
      })),
    byMonth: monthly,
    // Three-month moving average for the trend line.
    trend: monthly.map((m, i, arr) => {
      const from = Math.max(0, i - 2)
      const window = arr.slice(from, i + 1)
      return { month: m.month, emissions: m.total, trend_line: window.reduce((s, x) => s + x.total, 0) / window.length }
    }),
  }
}
